import xhr from '../xhr.js';

class UploadSession {
    #appId;
    #fileInput;

    #userAborted = false;
    #abortHandler;
    #progressHandler;

    constructor(appId, fileInput) {
        this.#appId = appId;
        this.#fileInput = fileInput;
    }

    abort() {
        this.#userAborted = true;
        if(typeof this.#abortHandler === 'function')
            this.#abortHandler();
    }

    onProgress(handler) {
        if(typeof handler !== 'function')
            throw 'handler must be a function';
        this.#progressHandler = handler;
    }

    #reportProgress() {
        if(this.#progressHandler !== undefined)
            this.#progressHandler({
                loaded: ev.loaded,
                total: ev.total,
                progress: ev.total <= 0 ? 0 : ev.loaded / ev.total,
            });
    }

    async start() {
        if(this.#userAborted)
            throw 'File upload was cancelled by the user, it cannot be restarted.';

        try {
            const formData = new FormData;
            formData.append('src', this.#appId);
            formData.append('file', this.#fileInput);

            const { status, body } = await xhr.post('/uploads', {
                type: 'json',
                authed: true,
                upload: this.#reportProgress,
                abort: handler => this.#abortHandler = handler,
            }, formData);
            if(body === null)
                throw "The upload server didn't return the metadata for some reason.";

            if(status !== 201)
                throw body.english ?? body.error ?? `Upload failed with status code ${status}`;

            body.isImage = () => body.type.startsWith('image/');
            body.isVideo = () => body.type.startsWith('video/');
            body.isAudio = () => body.type.startsWith('audio/');
            body.isMedia = () => body.isImage() || body.isAudio() || body.isVideo();

            return Object.freeze(body);
        } catch(ex) {
            if(this.#userAborted)
                throw '';

            console.error(ex);
            throw ex;
        }
    }
}

export default class UploadClient {
    #appId;

    constructor(appId) {
        if(typeof appId !== 'string')
            throw 'appId must be a string';
        this.#appId = appId
    }

    createUpload(fileInput) {
        if(!(fileInput instanceof File))
            throw 'fileInput must be an instance of window.File';

        return new UploadSession(this.#appId, fileInput);
    }

    async deleteUpload(fileInfo) {
        if(typeof fileInfo !== 'object')
            throw 'fileInfo must be an object';
        if(typeof fileInfo.urlf !== 'string')
            throw 'fileInfo.urlf must be a string';

        const { status, body } = await xhr.delete(fileInfo.urlf, {
            type: 'json',
            authed: true,
        });

        if(status !== 204) {
            if(body === null)
                throw `Delete failed with status code ${status}`;

            throw body.english ?? body.error ?? `Delete failed with status code ${status}`;
        }
    }
};
